// The one transport, and the policy every request carries.
//
// The generated client under ./gen knows the routes and the shapes and nothing
// else. What it does not know is decided here, once: where the API is mounted,
// how the caller proves who they are, which organisation they are acting in,
// and what a 401 means.
//
// mindvm had a hand-written api.ts of 1,896 lines against three base URLs —
// the protected API, /api/sdk and the operator API — each with its own auth.
// There is one base URL now and one credential, because the server collapsed
// its three APIs into one; this file is where that shows up on the client.
//
// # Three ways to prove who you are
//
// By default (`authit`) the bearer is the access token mindvm's own
// `POST /auth/login` returned: the server keeps the accounts itself. `zitadel`
// is the same thing against a hosted provider, for a deployment that already
// runs one. In development and in the end-to-end suite the server runs its
// header verifier (`header`), and the caller names their subject in
// X-Test-Subject instead — the same seam the Go suite uses. Which one is in
// force is a build-time setting (VITE_AUTH_MODE), never a runtime one, so a
// production bundle cannot be talked into the test seam.

import { client } from './gen/client.gen';

/** Where the API is mounted. The dev server proxies this to the Go binary. */
export const BASE = '/api';

export type AuthMode = 'authit' | 'header' | 'zitadel';

/**
 * How this build authenticates. `authit` — the app's own accounts — is the
 * default, so a build that says nothing gets the provider the product ships
 * with rather than one it has to be given. `header` is the test seam.
 */
export const AUTH_MODE: AuthMode =
  import.meta.env.VITE_AUTH_MODE === 'header'
    ? 'header'
    : import.meta.env.VITE_AUTH_MODE === 'zitadel'
      ? 'zitadel'
      : 'authit';

/**
 * Thrown for any non-2xx response. Carries the status and the parsed error
 * body, because the server's refusals are data rather than prose — a rejected
 * filter names the property that was wrong — and a form that discards that
 * throws away the half of the error that is actionable.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: Problem,
  ) {
    super(problem.message || problem.detail || problem.title || `HTTP ${status}`);
    this.name = 'ApiError';
  }
}

/** The error shape the server answers with: Huma's model, plus a code. */
export interface Problem {
  code?: string;
  message?: string;
  title?: string;
  status?: number;
  detail?: string;
  details?: { message: string; location?: string; value?: unknown }[];
  errors?: { message: string; location?: string; value?: unknown }[];
}

/** Reads the current credential: a bearer token, or a test subject. */
export type CredentialSource = () => string | null;

/**
 * Points the generated client at the API and gives it the caller's credential.
 * Call once, before anything uses the SDK; the source is a function because the
 * credential changes mid-session and a value captured here would be the one
 * that was current when the app started.
 *
 * X-Org-ID is deliberately NOT set here. The document says it is required, the
 * generated types say so too, and every call site passes it — see useOrg. An
 * interceptor that filled it in silently would make the type a lie and leave
 * "which organisation is this request for" invisible at the place it is
 * decided.
 */
export function configureClient(
  credential: CredentialSource,
  onUnauthenticated: () => void,
): void {
  client.setConfig({
    baseUrl: BASE,
    auth: AUTH_MODE !== 'header' ? () => credential() ?? undefined : undefined,
  });
  client.interceptors.request.use((request) => {
    if (AUTH_MODE === 'header') {
      const subject = credential();
      if (subject) request.headers.set('X-Test-Subject', subject);
    }
    return request;
  });
  client.interceptors.response.use(async (response) => {
    if (response.status === 401) onUnauthenticated();
    return response;
  });
}

/**
 * Unwraps a generated call into its value, or throws {@link ApiError}.
 *
 * The SDK answers `{ data, error }` rather than throwing, which is the wrong
 * default for TanStack Query: a query that resolves with an error object is a
 * query that reports success. This is the adapter, in one place.
 */
export async function unwrap<T>(
  call: Promise<{ data?: T; error?: unknown; response?: Response }>,
): Promise<T> {
  const { data, error, response } = await call;
  if (!response) {
    throw new ApiError(0, { title: 'Network error', detail: String(error ?? 'request failed') });
  }
  if (!response.ok) {
    throw new ApiError(response.status, (error as Problem) ?? { status: response.status });
  }
  return data as T;
}
